import { Predicate } from '@angular/core';
import { AsyncSubject, from, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

export class ConnectionCheck {

  // navigator.onLine is not reliable and can return true even if no connection available

  private static navigatorOnLineLastValue: boolean = undefined;
  private static navigatorOnLine$ = new ReplaySubject<boolean>(1);

  static {
    ConnectionCheck.navigatorOnLine$.subscribe((isConnected) => {
      ConnectionCheck.navigatorOnLineLastValue = isConnected;
    });

    window.addEventListener('online', () => ConnectionCheck.checkConnection());
    window.addEventListener('offline', () => ConnectionCheck.checkConnection());
  }

  private static timeout;

  private static refreshInterval = 30000;

  // this synchronously returns the last state. At startup, it may return true even if no connection available
  // this is called lots of times by Angular. But this is bad pattern, we should remove it
  public static get isConnection() {
    return ConnectionCheck.navigatorOnLineLastValue ?? !!navigator.onLine;
  }

  public static get isConnection$(): Observable<boolean> {
    return ConnectionCheck.navigatorOnLine$.asObservable();
  }

  // called asynchronously at startup and each time we suspect a connection change
  public static checkConnection() {
    clearTimeout(ConnectionCheck.timeout);
    ConnectionCheck.checkUrl().subscribe((isConnected) => {
      ConnectionCheck.setConnection(isConnected);
      ConnectionCheck.timeout = setTimeout(() => {
        ConnectionCheck.checkConnection();
      }, ConnectionCheck.refreshInterval);
    });
  }

  private static setConnection(isConnected: boolean) {
    if (typeof (ConnectionCheck.navigatorOnLineLastValue) === 'undefined') {
      ConnectionCheck.navigatorOnLine$.next(isConnected);
      return;
    }
    if (isConnected !== ConnectionCheck.navigatorOnLineLastValue) {
      console.log('connection changed', isConnected);
      window.dispatchEvent(new Event(isConnected ? 'customonline' : 'customoffline'));
      ConnectionCheck.navigatorOnLine$.next(isConnected);
    }
  }

  private static checkUrl(): Observable<boolean> {
    if (!window.navigator.onLine) {
      return of(false);
    }
    const url = new URL(window.location.origin);
    url.searchParams.set('d', Date.now().toString());

    try {
      return from(fetch(
        url.toString(),
        {
          method: 'HEAD',
          redirect: 'follow' // manual
        },
      )).pipe(
        map(r => {
          return r.ok || r.type === 'opaqueredirect';
        }),
        catchError(e => of(false))
      );
    } catch {
      return of(false);
    }
  }


}
